import { type FC, useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Card, Chip, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TFunction } from 'i18next';
import { bancaOreApi, BancaOreLedger } from 'api';

interface Props { employeeId?: number; seasonId?: number; t: TFunction; }

const h = (n: number) => `${Math.round(n * 10) / 10}h`;
const signed = (n: number) => (n > 0 ? `+${h(n)}` : h(n));

/**
 * Banca ore (monte ore): monthly planned-vs-contractual hours with a running balance.
 * Read-only; renders nothing when there is no data (existing tenants unaffected).
 */
const EmployeeBancaOre: FC<Props> = ({ employeeId, seasonId, t }) => {
  const [ledger, setLedger] = useState<BancaOreLedger | null>(null);

  useEffect(() => {
    let active = true;
    if (!employeeId) return;
    bancaOreApi.getLedger(employeeId, { seasonId })
      .then((res) => { if (active) setLedger(res.data); })
      .catch(() => { if (active) setLedger(null); });
    return () => { active = false; };
  }, [employeeId, seasonId]);

  if (!ledger?.rows.length) return null;
  // Hide if there is genuinely nothing planned and nothing expected.
  if (ledger.totals.expectedHours === 0 && ledger.totals.plannedHours === 0) return null;

  return (
    <Grid item md={12} xs={12}>
      <Card sx={{ p: 0 }}>
        {/* Collapsed by default: the balance stays visible in the header, but the
            detailed monthly table is hidden until expanded — keeps the earnings
            totals higher on the page and lets department heads keep it tucked
            away during closures while the numbers are still being refined. */}
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
              <Typography variant="h6">{t('bancaOre.title')}</Typography>
              <Chip
                color={ledger.totals.balance >= 0 ? 'success' : 'error'}
                label={`${t('bancaOre.balance')}: ${signed(ledger.totals.balance)}`}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pt: 0, pb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('bancaOre.month')}</TableCell>
                    <TableCell align="right">{t('bancaOre.expected')}</TableCell>
                    <TableCell align="right">{t('bancaOre.planned')}</TableCell>
                    <TableCell align="right">{t('bancaOre.delta')}</TableCell>
                    <TableCell align="right">{t('bancaOre.cumulative')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledger.rows.map((r) => (
                    <TableRow key={r.month}>
                      <TableCell>{r.month}</TableCell>
                      <TableCell align="right">{h(r.expectedHours)}</TableCell>
                      <TableCell align="right">{h(r.plannedHours)}</TableCell>
                      <TableCell align="right" sx={{ color: r.delta >= 0 ? 'success.main' : 'error.main' }}>{signed(r.delta)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: r.cumulative >= 0 ? 'success.main' : 'error.main' }}>{signed(r.cumulative)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Grid>
  );
};

export default EmployeeBancaOre;
